import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { Subject, filter, map, takeUntil, combineLatest } from 'rxjs';
import { FileDetailsFacade } from '@store/file-details/file-details.facade';
import { FileDetails } from '@store/file-details/file-details.state';
import { Permissions } from '../../core/enums';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';

@Component({
  selector: 'app-file-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, HasPermissionDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './file-details.component.html',
  styleUrls: ['./file-details.component.scss'],
})
export class FileDetailsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fileDetailsFacade = inject(FileDetailsFacade);

  file$ = this.fileDetailsFacade.file$;
  statuses$ = this.fileDetailsFacade.statuses$;
  isLoading$ = this.fileDetailsFacade.isLoading$;
  isUpdating$ = this.fileDetailsFacade.isUpdating$;
  error$ = this.fileDetailsFacade.error$;

  statusControl = new FormControl<number | null>(null, { nonNullable: false });

  private readonly destroy$ = new Subject<void>();
  public readonly Permissions = Permissions;

  ngOnInit(): void {
    this.fileDetailsFacade.loadFileStatuses();

    this.route.queryParamMap
      .pipe(
        map(params => {
          const idParam = params.get('id');
          return idParam ? parseInt(idParam, 10) : null;
        }),
        filter((id): id is number => id !== null && !isNaN(id)),
        takeUntil(this.destroy$)
      )
      .subscribe(id => {
        this.fileDetailsFacade.loadFileDetails(id);
      });

    combineLatest([this.file$, this.statuses$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([file, statuses]) => {
        if (file && statuses.length > 0) {
          this.statusControl.setValue(file.fileStatusId, { emitEvent: false });
        }
      });
  }

  onSave(file: FileDetails | null): void {
    if (!file) return;

    const newFileStatusId = this.statusControl.value;
    if (!newFileStatusId) return;

    this.fileDetailsFacade.updateFileStatus(file.id, newFileStatusId);
  }

  goBack(): void {
    window.history.back();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
